import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { addDoc, collection, doc, getDoc, updateDoc } from "firebase/firestore";
import { FC, FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useKrpano from "react-krpano-hooks";
import { db } from "../firebase/config";
import { Ad, Award, Match } from "../types";
import { WikiImageSearch } from "../components/WikiImageSearch";
import { zonesJSON } from "../hooks/zones";

const getImageDimensions = (url: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });

const emptyTeam = {
  teamName: "",
  teamUrlImage: "",
  teamColor: "#ff0000",
  teamColorSecondary: "#ffffff",
  assignedSeats: 0,
};

const AdminMatchEditor: FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const isEditing = !!matchId;
  const navigate = useNavigate();

  const [stadiumName, setStadiumName] = useState("");
  const [typeGame, setTypeGame] = useState("");
  const [totalSeats, setTotalSeats] = useState(648);
  const [local, setLocal] = useState({ ...emptyTeam });
  const [visitante, setVisitante] = useState({ ...emptyTeam, teamColor: "#0000ff" });
  const [awards, setAwards] = useState<Award[]>([]);
  const [hiddenAwards, setHiddenAwards] = useState<Record<string, string>>({});
  const [distributed, setDistributed] = useState(false);
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [wikiSearch, setWikiSearch] = useState<"local" | "visitante" | null>(null);
  const [wikiSearchAwardIdx, setWikiSearchAwardIdx] = useState<number | null>(null);
  const [wikiSearchAdIdx, setWikiSearchAdIdx] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [krpanoReady, setKrpanoReady] = useState(false);

  const { containerRef, callKrpano, setKrpano } = useKrpano({
    scriptPath: "/krpano/krpano.js",
    embeddingParams: { consolelog: true, xml: "/krpano/tour.xml" },
    width: "100%",
    height: "450px",
    handleLoaded: () => setKrpanoReady(true),
  });

  const prevAdIds = useRef<string[]>([]);

  useEffect(() => {
    if (!krpanoReady) return;

    prevAdIds.current.forEach((id) => {
      callKrpano(`removehotspot(ad_${id})`);
    });

    ads.forEach((ad) => {
      if (!ad.imageUrl) return;
      const hsName = `ad_${ad.id}`;
      callKrpano(`addhotspot(${hsName})`);
      setKrpano(`hotspot[${hsName}].url`, ad.imageUrl);
      setKrpano(`hotspot[${hsName}].ath`, ad.ath);
      setKrpano(`hotspot[${hsName}].atv`, ad.atv);
      setKrpano(`hotspot[${hsName}].width`, `${ad.width}`);
      setKrpano(`hotspot[${hsName}].height`, `${ad.height}`);
      setKrpano(`hotspot[${hsName}].distorted`, "true");
    });

    prevAdIds.current = ads.map((a) => a.id);
  }, [ads, callKrpano, setKrpano, krpanoReady]);

  useEffect(() => {
    if (!matchId) return;
    getDoc(doc(db, "match", matchId)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data() as Match;
        setStadiumName(data.stadiumName || "");
        setTypeGame(data.typeGame || "");
        setTotalSeats(data.totalSeats || 648);
        setLocal({
          teamName: data.local.teamName,
          teamUrlImage: data.local.teamUrlImage,
          teamColor: data.local.teamColor,
          teamColorSecondary: data.local.teamColorSecondary,
          assignedSeats: data.local.assignedSeats,
        });
        setVisitante({
          teamName: data.visitante.teamName,
          teamUrlImage: data.visitante.teamUrlImage,
          teamColor: data.visitante.teamColor,
          teamColorSecondary: data.visitante.teamColorSecondary,
          assignedSeats: data.visitante.assignedSeats,
        });
        setAwards(data.awards || []);
        setHiddenAwards(data.hiddenAwards || {});
        setDistributed(Object.keys(data.hiddenAwards || {}).length > 0);
        setAds(data.ads || []);
      }
      setLoading(false);
    });
  }, [matchId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const matchData = {
      stadiumName,
      typeGame,
      totalSeats,
      local,
      visitante,
      sillas: {},
    };

    if (isEditing) {
      const matchDoc = doc(db, "match", matchId);
      // Preserve existing sillas and assignedSeats when editing
      const snap = await getDoc(matchDoc);
      const existing = snap.data() as Match | undefined;
      await updateDoc(matchDoc, {
        stadiumName,
        typeGame,
        totalSeats,
        awards,
        hiddenAwards,
        ads,
        local: {
          ...local,
          assignedSeats: existing?.local.assignedSeats ?? local.assignedSeats,
        },
        visitante: {
          ...visitante,
          assignedSeats: existing?.visitante.assignedSeats ?? visitante.assignedSeats,
        },
      });
    } else {
      await addDoc(collection(db, "match"), {
        ...matchData,
        awards,
        hiddenAwards,
        ads,
        local: { ...local, assignedSeats: 0 },
        visitante: { ...visitante, assignedSeats: 0 },
      });
    }

    navigate("/admin");
  };

  const distributeAwards = () => {
    const totalAwards = awards.reduce((sum, a) => sum + a.quantity, 0);
    const zoneIds = zonesJSON.map((z) => z.id);

    // Shuffle zones
    const shuffled = [...zoneIds].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, Math.min(totalAwards, zoneIds.length));

    const newHidden: Record<string, string> = {};
    let idx = 0;
    for (const award of awards) {
      for (let i = 0; i < award.quantity && idx < selected.length; i++) {
        newHidden[selected[idx]] = award.id;
        idx++;
      }
    }

    setHiddenAwards(newHidden);
    setDistributed(true);
  };

  return (
    <Box sx={{ p: 4, bgcolor: "#1a1a2e", minHeight: "100vh" }}>
      <Button
        onClick={() => navigate("/admin")}
        sx={{ color: "#fff", mb: 2 }}
      >
        ← Volver
      </Button>
      <Typography variant="h4" color="white" gutterBottom>
        {isEditing ? "Editar partido" : "Crear partido"}
      </Typography>

      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
      {loading ? (
        <Box sx={{ color: "#fff", flex: 1 }}>Cargando...</Box>
      ) : (
      <Paper sx={{ p: 3, flex: "1 1 700px", maxWidth: 700 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            Información del partido
          </Typography>
          <TextField
            label="Nombre del estadio"
            fullWidth
            margin="normal"
            value={stadiumName}
            onChange={(e) => setStadiumName(e.target.value)}
            required
          />
          <TextField
            label="Tipo de juego"
            fullWidth
            margin="normal"
            value={typeGame}
            onChange={(e) => setTypeGame(e.target.value)}
            required
          />
          <TextField
            label="Total asientos"
            type="number"
            fullWidth
            margin="normal"
            value={totalSeats}
            onChange={(e) => setTotalSeats(Number(e.target.value))}
            required
          />

          {/* Local team */}
          <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
            Equipo Local
          </Typography>
          <TextField
            label="Nombre del equipo"
            fullWidth
            margin="normal"
            value={local.teamName}
            onChange={(e) => setLocal({ ...local, teamName: e.target.value })}
            required
          />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
            <TextField
              label="URL del logo"
              fullWidth
              margin="normal"
              value={local.teamUrlImage}
              onChange={(e) =>
                setLocal({ ...local, teamUrlImage: e.target.value })
              }
              required
            />
            <Button
              variant="outlined"
              onClick={() => setWikiSearch("local")}
              sx={{ whiteSpace: "nowrap", mt: 1 }}
            >
              Buscar imagen
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 1, alignItems: "center" }}>
            <TextField
              label="Color principal"
              type="color"
              value={local.teamColor}
              onChange={(e) =>
                setLocal({ ...local, teamColor: e.target.value })
              }
              sx={{ width: 150 }}
            />
            <TextField
              label="Color secundario"
              type="color"
              value={local.teamColorSecondary}
              onChange={(e) =>
                setLocal({ ...local, teamColorSecondary: e.target.value })
              }
              sx={{ width: 150 }}
            />
            {local.teamUrlImage && (
              <Box
                component="img"
                src={local.teamUrlImage}
                alt="Logo local"
                sx={{ height: 56, objectFit: "contain" }}
              />
            )}
          </Box>

          {/* Visitante team */}
          <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
            Equipo Visitante
          </Typography>
          <TextField
            label="Nombre del equipo"
            fullWidth
            margin="normal"
            value={visitante.teamName}
            onChange={(e) =>
              setVisitante({ ...visitante, teamName: e.target.value })
            }
            required
          />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
            <TextField
              label="URL del logo"
              fullWidth
              margin="normal"
              value={visitante.teamUrlImage}
              onChange={(e) =>
                setVisitante({ ...visitante, teamUrlImage: e.target.value })
              }
              required
            />
            <Button
              variant="outlined"
              onClick={() => setWikiSearch("visitante")}
              sx={{ whiteSpace: "nowrap", mt: 1 }}
            >
              Buscar imagen
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 1, alignItems: "center" }}>
            <TextField
              label="Color principal"
              type="color"
              value={visitante.teamColor}
              onChange={(e) =>
                setVisitante({ ...visitante, teamColor: e.target.value })
              }
              sx={{ width: 150 }}
            />
            <TextField
              label="Color secundario"
              type="color"
              value={visitante.teamColorSecondary}
              onChange={(e) =>
                setVisitante({
                  ...visitante,
                  teamColorSecondary: e.target.value,
                })
              }
              sx={{ width: 150 }}
            />
            {visitante.teamUrlImage && (
              <Box
                component="img"
                src={visitante.teamUrlImage}
                alt="Logo visitante"
                sx={{ height: 56, objectFit: "contain" }}
              />
            )}
          </Box>

          {/* Awards */}
          <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
            Premios ocultos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define los premios y su cantidad. Al distribuir, se asignan aleatoriamente a las zonas del estadio.
          </Typography>

          {awards.map((award, i) => (
            <Paper
              key={award.id}
              variant="outlined"
              sx={{ p: 2, mb: 2, bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">Premio {i + 1}</Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setAwards(awards.filter((_, j) => j !== i));
                    setDistributed(false);
                  }}
                >
                  Eliminar
                </Button>
              </Box>
              <TextField
                label="Nombre del premio"
                size="small"
                fullWidth
                value={award.label}
                onChange={(e) => {
                  const updated = [...awards];
                  updated[i] = { ...award, label: e.target.value };
                  setAwards(updated);
                }}
                placeholder="Camiseta oficial, Balón firmado..."
                sx={{ mb: 1 }}
              />
              <TextField
                label="Descripción"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={award.description}
                onChange={(e) => {
                  const updated = [...awards];
                  updated[i] = { ...award, description: e.target.value };
                  setAwards(updated);
                }}
                placeholder="Descripción que verá el usuario al ganar..."
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  label="Cantidad"
                  size="small"
                  type="number"
                  value={award.quantity}
                  onChange={(e) => {
                    const updated = [...awards];
                    updated[i] = { ...award, quantity: Math.max(1, Number(e.target.value)) };
                    setAwards(updated);
                    setDistributed(false);
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ width: 100 }}
                />
                <TextField
                  label="URL imagen (opcional)"
                  size="small"
                  value={award.imageUrl || ""}
                  onChange={(e) => {
                    const updated = [...awards];
                    updated[i] = { ...award, imageUrl: e.target.value };
                    setAwards(updated);
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setWikiSearchAwardIdx(i)}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Buscar
                </Button>
                {award.imageUrl && (
                  <Box
                    component="img"
                    src={award.imageUrl}
                    alt={award.label}
                    sx={{ height: 40, objectFit: "contain" }}
                  />
                )}
              </Box>
            </Paper>
          ))}

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                setAwards([
                  ...awards,
                  {
                    id: `award_${Date.now()}`,
                    label: "",
                    description: "",
                    quantity: 1,
                  },
                ])
              }
            >
              + Agregar premio
            </Button>

            {awards.length > 0 && (
              <Button
                variant="contained"
                size="small"
                color="secondary"
                onClick={distributeAwards}
              >
                {distributed ? "Redistribuir premios" : "Distribuir premios"}
              </Button>
            )}
          </Box>

          {distributed && (
            <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
              {Object.keys(hiddenAwards).length} premios distribuidos en {Object.keys(hiddenAwards).length} zonas aleatorias
            </Typography>
          )}

          {/* Ads */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, mb: 1 }}>
            <Typography variant="h6">
              Publicidad en el tour
            </Typography>
            {ads.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowPreview((v) => !v)}
              >
                {showPreview ? "Ocultar vista previa" : "Ver vista previa"}
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Agrega imágenes publicitarias que aparecerán en el recorrido 360°. Usa ath (horizontal 0–360) y atv (vertical -90 a 90) para posicionar.
          </Typography>

          {ads.map((ad, i) => (
            <Paper
              key={ad.id}
              variant="outlined"
              sx={{ p: 2, mb: 2, bgcolor: "#fafafa" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">
                  Anuncio {i + 1}{ad.label ? ` — ${ad.label}` : ""}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setAds(ads.filter((_, j) => j !== i))}
                >
                  Eliminar
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                <TextField
                  label="URL de imagen"
                  size="small"
                  fullWidth
                  value={ad.imageUrl}
                  onChange={(e) => {
                    const updated = [...ads];
                    updated[i] = { ...ad, imageUrl: e.target.value };
                    setAds(updated);
                  }}
                  onBlur={() => {
                    if (ad.imageUrl) {
                      getImageDimensions(ad.imageUrl).then(({ width, height }) => {
                        setAds((prev) => {
                          const updated = [...prev];
                          updated[i] = { ...updated[i], width, height };
                          return updated;
                        });
                      }).catch(() => {});
                    }
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setWikiSearchAdIdx(i)}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Buscar
                </Button>
                {ad.imageUrl && (
                  <Box
                    component="img"
                    src={ad.imageUrl}
                    alt={ad.label || "Ad"}
                    sx={{ height: 40, objectFit: "contain" }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                <TextField
                  label="Etiqueta"
                  size="small"
                  value={ad.label || ""}
                  onChange={(e) => {
                    const updated = [...ads];
                    updated[i] = { ...ad, label: e.target.value };
                    setAds(updated);
                  }}
                  placeholder="Sponsor A"
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  label="Link (opcional)"
                  size="small"
                  value={ad.link || ""}
                  onChange={(e) => {
                    const updated = [...ads];
                    updated[i] = { ...ad, link: e.target.value };
                    setAds(updated);
                  }}
                  placeholder="https://..."
                  sx={{ flex: 1, minWidth: 150 }}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <TextField
                  label="ath"
                  size="small"
                  type="number"
                  value={ad.ath}
                  onChange={(e) => {
                    const updated = [...ads];
                    updated[i] = { ...ad, ath: Number(e.target.value) };
                    setAds(updated);
                  }}
                  sx={{ width: 90 }}
                  inputProps={{ step: 0.1 }}
                />
                <TextField
                  label="atv"
                  size="small"
                  type="number"
                  value={ad.atv}
                  onChange={(e) => {
                    const updated = [...ads];
                    updated[i] = { ...ad, atv: Number(e.target.value) };
                    setAds(updated);
                  }}
                  sx={{ width: 90 }}
                  inputProps={{ step: 0.1 }}
                />
                <TextField
                  label="Ancho"
                  size="small"
                  type="number"
                  value={ad.width}
                  onChange={(e) => {
                    const updated = [...ads];
                    updated[i] = { ...ad, width: Number(e.target.value) };
                    setAds(updated);
                  }}
                  sx={{ width: 90 }}
                  inputProps={{ min: 1, step: 1 }}
                />
                <TextField
                  label="Alto"
                  size="small"
                  type="number"
                  value={ad.height}
                  onChange={(e) => {
                    const updated = [...ads];
                    updated[i] = { ...ad, height: Number(e.target.value) };
                    setAds(updated);
                  }}
                  sx={{ width: 90 }}
                  inputProps={{ min: 1, step: 1 }}
                />
              </Box>
            </Paper>
          ))}

          <Button
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            onClick={() =>
              setAds([
                ...ads,
                {
                  id: `ad_${Date.now()}`,
                  imageUrl: "",
                  ath: 0,
                  atv: 0,
                  width: 100,
                  height: 50,
                },
              ])
            }
          >
            + Agregar anuncio
          </Button>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 4 }}
          >
            {isEditing ? "Guardar cambios" : "Crear partido"}
          </Button>
        </form>
      </Paper>
      )}

      <Paper
        sx={{
          flex: "1 1 400px",
          position: "sticky",
          top: 16,
          overflow: "hidden",
          minWidth: showPreview ? 300 : 0,
          width: showPreview ? "auto" : 0,
          p: showPreview ? undefined : 0,
          border: showPreview ? undefined : "none",
          opacity: showPreview ? 1 : 0,
          pointerEvents: showPreview ? "auto" : "none",
        }}
      >
        <Typography variant="subtitle2" sx={{ p: 1.5, pb: 0 }}>
          Vista previa 360°
        </Typography>
        <Box ref={containerRef as any} />
      </Paper>
      </Box>

      <WikiImageSearch
        open={!!wikiSearch && wikiSearchAwardIdx === null}
        onClose={() => setWikiSearch(null)}
        onSelect={(url) => {
          if (wikiSearch === "local") {
            setLocal({ ...local, teamUrlImage: url });
          } else if (wikiSearch === "visitante") {
            setVisitante({ ...visitante, teamUrlImage: url });
          }
        }}
      />

      <WikiImageSearch
        open={wikiSearchAwardIdx !== null && wikiSearchAdIdx === null}
        onClose={() => setWikiSearchAwardIdx(null)}
        onSelect={(url) => {
          if (wikiSearchAwardIdx !== null) {
            const updated = [...awards];
            updated[wikiSearchAwardIdx] = { ...updated[wikiSearchAwardIdx], imageUrl: url };
            setAwards(updated);
          }
        }}
      />

      <WikiImageSearch
        open={wikiSearchAdIdx !== null}
        onClose={() => setWikiSearchAdIdx(null)}
        onSelect={(url) => {
          if (wikiSearchAdIdx !== null) {
            const idx = wikiSearchAdIdx;
            const updated = [...ads];
            updated[idx] = { ...updated[idx], imageUrl: url };
            setAds(updated);
            getImageDimensions(url).then(({ width, height }) => {
              setAds((prev) => {
                const u = [...prev];
                u[idx] = { ...u[idx], width, height };
                return u;
              });
            }).catch(() => {});
          }
        }}
      />
    </Box>
  );
};

export default AdminMatchEditor;
